import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { User, Building2, History } from "lucide-react";
import CustomFieldInput from "@/components/leads/CustomFieldInput";

/** POC + Head Office cards — extracted from LeadDetail.jsx (refactor Phase 3). */
export default function PocHqCards({
  lead, form, setForm, show, req, ro, labelOf,
  customFields, dropdowns, handleTogglePocConfirm,
}) {
  return (
    <>
        {/* POC Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <User className="w-4 h-4 text-slate-500" /> POC Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox
                checked={form.poc_confirmed}
                onCheckedChange={handleTogglePocConfirm}
                data-testid="poc-confirmed-checkbox"
              />
              The above contact is the correct POC (auto-fill from upload)
            </label>
            <div className="grid grid-cols-2 gap-3">
              {show("poc_name") && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                  {labelOf("poc_name", "POC Name")}{req("poc_name") && <span className="text-rose-500 ml-0.5">*</span>}
                </Label>
                <Input
                  value={form.poc_name}
                  disabled={ro("poc_name")}
                  onChange={(e) => setForm((f) => ({ ...f, poc_name: e.target.value }))}
                  className="h-8 mt-1 text-xs"
                  data-testid="poc-name-input"
                />
              </div>
              )}
              {show("poc_phone") && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                  {labelOf("poc_phone", "POC Contact")}{req("poc_phone") && <span className="text-rose-500 ml-0.5">*</span>}
                </Label>
                <Input
                  value={form.poc_phone}
                  disabled={ro("poc_phone")}
                  onChange={(e) => setForm((f) => ({ ...f, poc_phone: e.target.value }))}
                  className="h-8 mt-1 text-xs"
                  data-testid="poc-phone-input"
                />
              </div>
              )}
              {show("poc_email") && (
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                  {labelOf("poc_email", "POC Email")}{req("poc_email") && <span className="text-rose-500 ml-0.5">*</span>}
                </Label>
                <Input
                  type="email"
                  value={form.poc_email}
                  disabled={ro("poc_email")}
                  onChange={(e) => setForm((f) => ({ ...f, poc_email: e.target.value }))}
                  className="h-8 mt-1 text-xs"
                  data-testid="poc-email-input"
                />
              </div>
              )}
              {customFields.filter((f) => f.section === "poc").map((f) => (
                <CustomFieldInput key={f.id} field={f} value={(form.custom || {})[f.key] || ""}
                  dropdowns={dropdowns}
                  onChange={(v) => setForm((prev) => ({ ...prev, custom: { ...(prev.custom || {}), [f.key]: v } }))} />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Head Office */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-500" /> Head Office
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <RadioGroup
              value={form.hq_has}
              onValueChange={(v) => setForm((f) => ({ ...f, hq_has: v }))}
              className="flex gap-4"
            >
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <RadioGroupItem value="yes" data-testid="hq-has-yes" /> Have Head Office Details
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <RadioGroupItem value="no" data-testid="hq-has-no" /> Don&apos;t Have
              </label>
            </RadioGroup>
            <div className={`grid grid-cols-2 gap-3 ${form.hq_has === "yes" ? "" : "opacity-40 pointer-events-none"}`}>
              {show("hq_name") && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                  {labelOf("hq_name", "HQ Name")}{req("hq_name") && <span className="text-rose-500 ml-0.5">*</span>}
                </Label>
                <Input
                  value={form.hq_name}
                  onChange={(e) => setForm((f) => ({ ...f, hq_name: e.target.value }))}
                  className="h-8 mt-1 text-xs"
                  data-testid="hq-name-input"
                  disabled={form.hq_has !== "yes" || ro("hq_name")}
                />
              </div>
              )}
              {show("hq_phone") && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                  {labelOf("hq_phone", "HQ Phone")}{req("hq_phone") && <span className="text-rose-500 ml-0.5">*</span>}
                </Label>
                <Input
                  value={form.hq_phone}
                  onChange={(e) => setForm((f) => ({ ...f, hq_phone: e.target.value }))}
                  className="h-8 mt-1 text-xs"
                  data-testid="hq-phone-input"
                  disabled={form.hq_has !== "yes" || ro("hq_phone")}
                />
              </div>
              )}
              {show("hq_email") && (
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-slate-400">
                  {labelOf("hq_email", "HQ Email")}{req("hq_email") && <span className="text-rose-500 ml-0.5">*</span>}
                </Label>
                <Input
                  type="email"
                  value={form.hq_email}
                  onChange={(e) => setForm((f) => ({ ...f, hq_email: e.target.value }))}
                  className="h-8 mt-1 text-xs"
                  data-testid="hq-email-input"
                  disabled={form.hq_has !== "yes" || ro("hq_email")}
                />
              </div>
              )}
              {customFields.filter((f) => f.section === "head_office").map((f) => (
                <CustomFieldInput key={f.id} field={f} value={(form.custom || {})[f.key] || ""}
                  dropdowns={dropdowns}
                  onChange={(v) => setForm((prev) => ({ ...prev, custom: { ...(prev.custom || {}), [f.key]: v } }))} />
              ))}
            </div>
            {lead.head_office?.propagated_from && (
              <div className="text-[10px] text-slate-500 bg-slate-50 border rounded p-2">
                <History className="w-3 h-3 inline mr-1" />
                HQ inherited from <strong>{lead.head_office.propagated_from}</strong>. Manual edits override propagation.
              </div>
            )}
          </CardContent>
        </Card>
    </>
  );
}
